import { SSOProvider } from '@app/models/auth/sso-provider.enum';
import { HRLOCKER, SignInProvider } from '@app/models/auth/sign-in-provider';
import { environment } from 'environments/environment';

// The Google/Microsoft consent links. The sign-in link is shared by the login page and the calendar Reconnect on the
// Notifications page; both sign in with calendar access, so completing either one also stores the calendar token. The
// calendar connect link is for a user whose sign-in doesn't link a calendar.

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar';
// What a Microsoft calendar connect asks for: calendar access only, with offline access so Microsoft issues a refresh
// token. Never the sign-in scopes; the account is read from the calendar itself.
const MICROSOFT_CONNECT_SCOPES = 'offline_access+calendars.readwrite';

// Where the SSO callback returns a Reconnect to. Fixed rather than carried in the sign-in, so nothing from outside the
// app can choose where the user lands.
export const SSO_RECONNECT_RETURN_URL = '/settings/connections';

// Marks a Reconnect in the OAuth state, which the provider sends back to the callback unchanged: "GOOGLE:reconnect:42",
// where 42 is the user who started it. A plain login's state is just the provider name. Carrying it in the state rather
// than in storage means an abandoned Reconnect leaves nothing behind to redirect a later sign-in.
const RECONNECT_MARKER = 'reconnect';

// Marks a calendar connect the same way ("GOOGLE:connect:42:<check>"): the callback hands its code to the calendar
// connect endpoint instead of signing in, so the user stays signed in as themselves. The last part is a random value
// this tab keeps until the callback, so a connect link built anywhere else (to store someone else's calendar on the
// signed-in user) has no matching value and is refused.
const CONNECT_MARKER = 'connect';
const CONNECT_CHECK_KEY = 'calendar_connect_check';

/** Starts a calendar connect in this tab: a fresh random value for its state, kept until the callback reads it. */
export function startCalendarConnect(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const check = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  sessionStorage.setItem(CONNECT_CHECK_KEY, check);
  return check;
}

/** The value this tab's calendar connect started with, removed as it's read so it is good for one callback only. */
export function takeCalendarConnectCheck(): string | null {
  const check = sessionStorage.getItem(CONNECT_CHECK_KEY);
  sessionStorage.removeItem(CONNECT_CHECK_KEY);
  return check;
}

export interface SsoAuthorizeOptions {
  // Started from Reconnect by this user, so the callback returns to the Notifications page instead of the home page,
  // and refuses the result if someone else ends up signed in at the provider.
  reconnectUserId?: number;
  // Google only issues a new refresh token when the consent screen is shown, so a reconnect must force it; without it
  // the sign-in succeeds but the lost calendar access isn't restored.
  forceConsent?: boolean;
  // Pre-selects the signed-in user's own account, so a wrong one isn't picked by mistake.
  loginHint?: string;
}

// HRLocker signs people in on its own page rather than through our OAuth flow, so it takes none of the options.
export function ssoAuthorizeUrl(provider: SignInProvider, options: SsoAuthorizeOptions = {}): string {
  if (provider === HRLOCKER) {
    return environment.sso.hrLocker.loginUrl;
  }
  const state = buildSsoState(provider, options.reconnectUserId);
  const loginHint = options.loginHint ? `&login_hint=${encodeURIComponent(options.loginHint)}` : '';
  switch (provider) {
    case SSOProvider.MICROSOFT:
      return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=select_account&client_id=${environment.sso.microsoft.clientId}&response_type=code&redirect_uri=${environment.sso.redirectUri}&scope=openid+email+profile+offline_access+calendars.readwrite&state=${state}${loginHint}`;
    case SSOProvider.GOOGLE:
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${environment.sso.google.clientId}&redirect_uri=${environment.sso.redirectUri}&scope=openid+email+profile+${GOOGLE_CALENDAR_SCOPE}&response_type=code&state=${state}&access_type=offline${options.forceConsent ? '&prompt=consent' : ''}${loginHint}`;
    default:
      return undefined;
  }
}

/**
 * The consent link for connecting a calendar from the Notifications page, with the user's own account pre-selected.
 * Google: calendar access only, never the sign-in scopes, with the consent screen forced so Google issues a refresh
 * token. Microsoft: see MICROSOFT_CONNECT_SCOPES, with the account picker shown like a sign-in;
 * offline_access is what makes Microsoft issue the refresh token. Neither signs the user in.
 */
export function calendarConnectUrl(provider: SSOProvider, userId: number, check: string, loginHint?: string): string {
  const state = encodeURIComponent([provider, CONNECT_MARKER, userId, check].join(':'));
  const hint = loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : '';
  switch (provider) {
    case SSOProvider.MICROSOFT:
      return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=select_account&client_id=${environment.sso.microsoft.clientId}&response_type=code&redirect_uri=${environment.sso.redirectUri}&scope=${MICROSOFT_CONNECT_SCOPES}&state=${state}${hint}`;
    case SSOProvider.GOOGLE:
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${environment.sso.google.clientId}&redirect_uri=${environment.sso.redirectUri}&scope=${GOOGLE_CALENDAR_SCOPE}&response_type=code&state=${state}&access_type=offline&prompt=consent${hint}`;
    default:
      return undefined;
  }
}

export interface SsoState {
  provider: SSOProvider | undefined;
  reconnect: boolean;
  // The user who started the Reconnect, or null when it isn't one or the id is missing or malformed.
  reconnectUserId: number | null;
  connect: boolean;
  // The user who started the calendar connect, or null when it isn't one or the id is missing or malformed.
  connectUserId: number | null;
  // The calendar connect's random value, or null when it isn't one or the value is missing.
  connectCheck: string | null;
}

/** Builds the state sent with the sign-in, URL-encoded: the provider name, or "GOOGLE:reconnect:42" for a Reconnect. */
function buildSsoState(provider: SSOProvider, reconnectUserId?: number | null): string {
  const reconnecting = reconnectUserId !== undefined && reconnectUserId !== null;
  return encodeURIComponent(reconnecting ? [provider, RECONNECT_MARKER, reconnectUserId].join(':') : provider);
}

/**
 * Reads the state the provider sent back: which provider it was, and whether (and by whom) a Reconnect or a calendar
 * connect started it.
 */
export function parseSsoState(state: string): SsoState {
  const [name, marker, id, check] = state.split(':');
  const userId = /^\d+$/.test(id || '') ? Number(id) : null;
  const reconnect = marker === RECONNECT_MARKER;
  const connect = marker === CONNECT_MARKER;
  return {
    provider: SSOProvider[name as keyof typeof SSOProvider],
    reconnect,
    reconnectUserId: reconnect ? userId : null,
    connect,
    connectUserId: connect ? userId : null,
    connectCheck: connect && check ? check : null
  };
}
